import { useState, useEffect, useCallback } from 'react';
import { Download, X, Share } from 'lucide-react';

const DISMISS_KEY = 'pwa-install-dismissed';
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

export const InstallAppBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    // Check if already running as installed PWA
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    setIsStandalone(standalone);
    if (standalone) return;

    // Check dismissal (only stores UI preference timestamp, not sensitive data)
    try {
      const dismissed = localStorage.getItem(DISMISS_KEY);
      if (dismissed && Date.now() - parseInt(dismissed) < DISMISS_DURATION) return;
    } catch (error) {
      // localStorage may be unavailable in some contexts
    }

    // Detect iOS
    const ua = navigator.userAgent;
    const isiOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(isiOS);

    if (isiOS) {
      // Show iOS instructions after a short delay
      const timer = setTimeout(() => setShowBanner(true), 3000);
      return () => clearTimeout(timer);
    }

    // Android/Desktop: listen for beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowBanner(true), 2000);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
    setInstalling(false);
  }, [deferredPrompt]);

  const handleDismiss = () => {
    setShowBanner(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* localStorage unavailable */ }
  };

  if (isStandalone || !showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100] animate-in slide-in-from-bottom duration-500" data-testid="install-app-banner">
      <div className="bg-[#000000] border-t border-[#222222] shadow-2xl">
        <div className="max-w-lg mx-auto px-4 py-4">
          <div className="flex items-start gap-4">
            {/* App Icon */}
            <div className="w-14 h-14 rounded-xl bg-[#DEA193] flex items-center justify-center flex-shrink-0 shadow-lg">
              <span className="text-[#000000] text-lg font-bold leading-none">SS</span>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="text-white font-medium text-sm">Split Soul Active</h3>
                  <p className="text-[#737373] text-xs mt-0.5">Get the app for a faster experience</p>
                </div>
                <button
                  onClick={handleDismiss}
                  className="text-[#737373] hover:text-white p-1 -mt-1 -mr-1 transition-colors"
                  data-testid="install-banner-dismiss"
                  aria-label="Dismiss"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {isIOS ? (
                <div className="mt-3 flex items-center gap-2 bg-[#222222] rounded-lg px-3 py-2.5">
                  <Share className="w-4 h-4 text-[#DEA193] flex-shrink-0" />
                  <p className="text-xs text-[#A3A3A3] leading-snug">
                    Tap <span className="text-white font-medium">Share</span> then <span className="text-white font-medium">&ldquo;Add to Home Screen&rdquo;</span>
                  </p>
                </div>
              ) : (
                <button
                  onClick={handleInstall}
                  disabled={installing}
                  className="mt-3 w-full flex items-center justify-center gap-2 bg-white text-[#000000] text-xs font-medium tracking-wider py-2.5 px-4 hover:bg-[#DEA193] transition-colors disabled:opacity-50"
                  data-testid="install-app-button"
                >
                  <Download className="w-3.5 h-3.5" />
                  {installing ? 'Installing...' : 'Install App'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
